import { fabric } from "fabric";

export interface PlaceholderSize {
  width: number;
  height: number;
}

interface PredictionContentDimensions {
  classifications?: { im_width?: unknown; im_height?: unknown } | null;
  /** ML output image size as [width, height]. */
  size?: unknown;
}

const NO_SIZE: PlaceholderSize = { width: 0, height: 0 };

function toPositive(value: unknown): number {
  const number = Number(value);
  return number > 0 ? number : 0;
}

/**
 * Pixel size the ML geometry was produced on. PHI-restricted users get a fixed 200×200
 * placeholder instead of the radiograph, so the canvas must be sized from the prediction.
 * Candidates are taken as width/height pairs so dimensions from different sources never mix.
 */
export function getPlaceholderSize(
  content?: PredictionContentDimensions | null,
  explicit?: { width?: unknown; height?: unknown } | null
): PlaceholderSize {
  const size = Array.isArray(content?.size) ? content?.size : [];
  const candidates: Array<[unknown, unknown]> = [
    [explicit?.width, explicit?.height],
    [content?.classifications?.im_width, content?.classifications?.im_height],
    [size[0], size[1]],
  ];
  for (const [rawWidth, rawHeight] of candidates) {
    const width = toPositive(rawWidth);
    const height = toPositive(rawHeight);
    if (width && height) {
      return { width, height };
    }
  }
  return NO_SIZE;
}

/** Redraws the placeholder at the prediction's pixel size so annotations line up. */
export function stretchToPlaceholderSize(
  image: fabric.Image,
  { width, height }: PlaceholderSize,
  options?: fabric.IImageOptions
): fabric.Image {
  const element = image.getElement();
  if (!width || !height || !element) {
    return image;
  }
  const offscreen = document.createElement("canvas");
  offscreen.width = width;
  offscreen.height = height;
  offscreen.getContext("2d")?.drawImage(element, 0, 0, width, height);
  // fabric.Image accepts HTMLCanvasElement at runtime; @types/fabric does not.
  return new fabric.Image(offscreen as unknown as HTMLImageElement, options);
}
